import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { useMemo } from 'react'

import { Box } from '../../components/Box'
import { Button } from '../../components/Button'
import { Flex } from '../../components/Flex'
import { Price } from '../../components/Price'
import { Text } from '../../components/Text'
import { ErrorText } from '../../components/Text/ErrorText'
import { themeColor } from '../../components/theme'
import { TaxesFootnote } from '../../containers/TaxesFootnote'
import { getPricesMode } from '../../redux/config'
import { DomainType } from '../../redux/domains'
import { getDomains } from '../../redux/domains/selectors'
import {
  getOrderId,
  getOrderItems,
  isAnyCustomerRequestError,
  isLoadingOrderItems,
  isOrderLoading,
  isRemovingOrderItem,
  OrderActions,
  OrderItem,
  OrderItemStatusCode
} from '../../redux/order'
import { RootState } from '../../redux/types'
import { getContractType } from '../../redux/user/selectors'
import { createTFunction } from '../../utils/helpers'
import { ColumnType } from '../../components/Table'
import { useTablePagination, useTableSorting } from '../../redux/table/hooks'
import { ReportUmbracoData } from '../CompanyDetail/Reports'
import { useApi, useDateFormat, useViewportSize } from '../../hooks'
import { Image } from '../../components/Image'
import { Icon } from '../../components/Icons'
import { Space } from '../../components/Space'
import { DialogActions } from '../../redux/dialog'
import { GetTypedSmallTextTable } from '../../components/SmallTextTable/SmallTextTable'
import { CompanyLink } from '../../components/CompanyLink/CompanyLink'

import { CartItemsProps } from './CartItemsProps'
import { CartItemError } from './CartItemError'
import { CartPrice } from './CartPrice'

const SmallTextCartTable = GetTypedSmallTextTable<OrderItem>()

const TableWrapper = styled(Flex)`
  background-color: ${themeColor('white')};
  z-index: 5;
  position: relative;
`

const CartItemErrorBox = styled(Box)`
  color: ${themeColor('danger')};
  font-weight: bold;
  align-self: center;
`

const CompanyName = styled.div`
  font-weight: bold;
  color: ${themeColor('primary')};
`

const OrderErrorWarning = styled(Box)`
  color: ${themeColor('danger')};
  font-weight: bold;
`

const Alert = styled.div`
  display: flex;
  align-items: center;
`

const AlertIcon = styled(Icon)`
  font-size: 20px;
  margin-right: 8px;
`

const requiredDomains = [DomainType.Culture, DomainType.DeliveryOption]

export const CartTable = ({ productsUmbracoData, accountingUnavailable: accountingUnavailable }: CartItemsProps) => {
  const viewPort = useViewportSize()
  const isMobile = viewPort?.isMobile ?? false
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation(['cart', 'import'])
  const tc = createTFunction('cart:', t)
  const tit = createTFunction('import:Table.', t)
  const { formatDate } = useDateFormat()

  const pricesMode = useSelector(getPricesMode)
  const contractType = useSelector(getContractType)

  const items = useSelector(getOrderItems)

  const orderId = useSelector(getOrderId)
  const isCustomerRequestError = useSelector(isAnyCustomerRequestError)
  const orderLoading = useSelector(isOrderLoading)
  const isRemoving = useSelector((state: any) => isRemovingOrderItem(state))
  const isLoading = useSelector((state: any) =>
    isLoadingOrderItems(state, { customerRequestIds: _.map(items, i => i.customerRequestId) })
  )
  const { request: deleteAllRequest, loading: deletingAll } = useApi()

  const domainsData = useSelector((state: RootState) =>
    getDomains(state, {
      domains: requiredDomains,
      language: i18n.language
    })
  )

  const allWithError = _.every(items, i => i.statusCode === OrderItemStatusCode.Error)

  const tableOptions = {
    ...useTableSorting('cart'),
    ...useTablePagination('cart')
  }

  const renderCompanyLink = useMemo(
    // eslint-disable-next-line react/display-name
    () => (_: string, record: OrderItem) =>
      record.companyIdentifier ? (
        <CompanyLink
          companyIdentifier={record.countryCode + '-' + record.companyIdentifier}
          companyName={record.companyName}
          companyIdentifierType={record.identifierType}
        >
          <Image type="arrowLeftBlue" />
        </CompanyLink>
      ) : null,
    []
  )

  const renderCompanyName = useMemo(
    // eslint-disable-next-line react/display-name
    () => (_: string, record: OrderItem) => <CompanyName>{record.companyName}</CompanyName>,
    []
  )

  const renderReport = useMemo(
    // eslint-disable-next-line react/display-name
    () => (_value: string, record: OrderItem) =>
      _.find(productsUmbracoData, (up: ReportUmbracoData) => up.bifCode === record.reportType)?.name ??
      record.reportName,
    [productsUmbracoData]
  )

  const renderDeliveryOption = useMemo(
    // eslint-disable-next-line react/display-name
    () => (_value: string, record: OrderItem) =>
      _.find(domainsData.DeliveryOption, ddo => ddo.code === record.deliveryOptionCode)?.description ??
      record.deliveryOptionCode,
    [domainsData.DeliveryOption]
  )

  const renderLanguage = useMemo(
    // eslint-disable-next-line react/display-name
    () => (_value: string, record: OrderItem) =>
      _.find(domainsData.Culture, culture => culture.code === record.languageCode)?.description ?? record.languageCode,
    [domainsData.Culture]
  )

  const renderLastUpdate = useMemo(
    // eslint-disable-next-line react/display-name
    () => (_: string, record: OrderItem) => formatDate(record.lastUpdateDate, { dateStyle: 'medium' }),
    [formatDate]
  )

  const renderPrice = useMemo(
    // eslint-disable-next-line react/display-name
    () => (_: string, record: OrderItem) => (
      <Text color="secondary" bold noWrap>
        {Price({
          pricesMode,
          contractType,
          grossValue: record.valueWithTaxFormatted,
          netValue: record.netValueFormatted
        })}
      </Text>
    ),
    [contractType, pricesMode]
  )

  const renderAdditionalInfo = useMemo(
    // eslint-disable-next-line react/display-name
    () => (_value: string, record: OrderItem) =>
      record.statusCode === OrderItemStatusCode.Error ? (
        <CartItemErrorBox>{CartItemError(record)}</CartItemErrorBox>
      ) : (
        _.find(productsUmbracoData, (up: ReportUmbracoData) => up.bifCode === record.reportType)?.additionalInfo
      ),
    [productsUmbracoData]
  )

  const renderDelete = useMemo(
    // eslint-disable-next-line react/display-name
    () => (_value: string, record: OrderItem) => (
      <Button
        variant="primary"
        size="small"
        padding="5px 12px"
        onClick={() => dispatch(OrderActions.removeOrderItem({ customerRequestId: record.customerRequestId }))}
        disabled={isRemoving || deletingAll}
        loading={isLoading[record.customerRequestId] ?? false}
      >
        <Icon type="Trash" />
      </Button>
    ),
    [dispatch, isLoading, isRemoving, deletingAll]
  )

  const tableColumns: ColumnType<OrderItem>[] = useMemo(
    () =>
      _.concat<ColumnType<OrderItem>>([
        {
          id: 'companyLink',
          render: renderCompanyLink
        },
        {
          id: 'company',
          dataIndex: 'companyName',
          title: tit('CompanyName'),
          render: renderCompanyName,
          sorter: true
        },
        {
          id: 'productName',
          dataIndex: 'reportType',
          title: tit('ReportName'),
          render: renderReport,
          sorter: true
        },
        {
          id: 'reportAvailabilityCode',
          dataIndex: 'deliveryOptionCode',
          title: tit('DeliveryOption'),
          render: renderDeliveryOption,
          sorter: true
        },
        {
          id: 'lastUpdateDate',
          dataIndex: 'lastUpdateDate',
          title: tit('LastUpdate'),
          render: renderLastUpdate,
          sorter: true
        },
        {
          id: 'language',
          dataIndex: 'languageCode',
          title: tit('Language'),
          render: renderLanguage,
          sorter: true
        },
        {
          id: 'price',
          title: tit('Price'),
          render: renderPrice,
          sorter: true
        },
        {
          id: 'additionalInfo',
          dataIndex: 'statusCode',
          title: tc('AdditionalInfo'),
          render: renderAdditionalInfo,
          sorter: false
        },
        {
          id: 'delete',
          render: renderDelete
        }
      ]),
    [
      renderAdditionalInfo,
      renderCompanyLink,
      renderCompanyName,
      renderDelete,
      renderDeliveryOption,
      renderLanguage,
      renderLastUpdate,
      renderPrice,
      renderReport,
      tc,
      tit
    ]
  )

  return (
    <Space flexDirection="column" direction="vertical">
      <TableWrapper mt="40px" flexDirection="column" mx="-24px">
        {accountingUnavailable && (
          <Alert>
            <AlertIcon color="warning" type="ExclamationCircleOutlined" />
            {t('cart:ServiceUnavailable')}
          </Alert>
        )}
        {isCustomerRequestError && <OrderErrorWarning mb={1}>{tc('ErrorInCartItems')}</OrderErrorWarning>}
        <SmallTextCartTable
          rowKey="customerRequestId"
          dataSource={items}
          columns={tableColumns}
          loading={orderLoading}
          {...tableOptions}
        />
        {allWithError && (
          <Box px={3} pb={1} pt={1}>
            <ErrorText>{tc('CartProcessingError')}</ErrorText>
          </Box>
        )}
      </TableWrapper>
      <Space
        justifyContent="space-between"
        flexDirection={isMobile ? 'column' : 'row'}
        direction={isMobile ? 'vertical' : 'horizontal'}
        space={4}
      >
        <Box mt={isMobile ? '11px' : '44px'}>
          <Button
            variant="primary"
            padding="5px 12px"
            onClick={async () => {
              try {
                await deleteAllRequest({ url: `/api/ordering/order/${orderId}/customerRequests/all`, method: 'delete' })
                dispatch(
                  OrderActions.loadOrder({
                    page: 1,
                    pageSize: undefined
                  })
                )
                dispatch(OrderActions.loadOrderMetadata())
              } catch {
                dispatch(
                  DialogActions.showInfoDialog({
                    title: tc('DeleteAllErrorTitle'),
                    text: tc('DeleteAllErrorText')
                  })
                )
              }
            }}
            disabled={isRemoving || deletingAll}
            loading={deletingAll}
            icon={<Icon type="Trash" />}
          >
            {tc('DeleteAllItems')}
          </Button>
        </Box>
        <CartPrice />
      </Space>
      <TaxesFootnote wrapperProps={{ block: true, textAlign: 'right' }} />
    </Space>
  )
}
